import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Calendar, User } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ToothNotesModal = ({ isOpen, onClose, toothNumber, existingNote, onSave, onDelete }) => {
  const [note, setNote] = useState('');
  const [condition, setCondition] = useState('healthy');
  const [treatment, setTreatment] = useState('');

  useEffect(() => {
    if (existingNote) {
      setNote(existingNote?.note || '');
      setCondition(existingNote?.condition || 'healthy');
      setTreatment(existingNote?.treatment || '');
    } else {
      setNote('');
      setCondition('healthy');
      setTreatment('');
    }
  }, [existingNote, toothNumber]);

  const handleSave = () => {
    const noteData = {
      toothNumber,
      note,
      condition,
      treatment,
      timestamp: new Date()?.toISOString(),
      provider: 'Dr. Chen' // In real app, get from auth context
    };
    onSave(noteData);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm(`Delete notes for tooth #${toothNumber}?`)) {
      onDelete(toothNumber);
      onClose();
    }
  };

  if (!isOpen) return null;

  const conditions = [
    { value: 'healthy', label: 'Healthy', color: 'text-green-600' },
    { value: 'caries', label: 'Caries', color: 'text-red-600' },
    { value: 'restoration', label: 'Restoration', color: 'text-blue-600' },
    { value: 'crown', label: 'Crown', color: 'text-purple-600' },
    { value: 'root_canal', label: 'Root Canal', color: 'text-orange-600' },
    { value: 'missing', label: 'Missing', color: 'text-gray-600' },
    { value: 'implant', label: 'Implant', color: 'text-indigo-600' },
    { value: 'fracture', label: 'Fracture', color: 'text-red-700' },
    { value: 'periodontal', label: 'Periodontal Issue', color: 'text-yellow-600' },
    { value: 'other', label: 'Other', color: 'text-gray-700' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Tooth #{toothNumber} Notes
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Enter clinical observations and treatment notes
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Condition Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Condition Status
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {conditions?.map((cond) => (
                <button
                  key={cond?.value}
                  onClick={() => setCondition(cond?.value)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    condition === cond?.value
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <span className={condition === cond?.value ? 'text-blue-700' : cond?.color}>
                    {cond?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Treatment/Procedure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Treatment/Procedure
            </label>
            <input
              type="text"
              value={treatment}
              onChange={(e) => setTreatment(e?.target?.value)}
              placeholder="e.g., Composite restoration, Crown prep, Extraction"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Clinical Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinical Notes
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              placeholder="Enter detailed observations, findings, or treatment notes..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Existing note metadata */}
          {existingNote && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Last updated: {new Date(existingNote.timestamp)?.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>Provider: {existingNote?.provider}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div>
            {existingNote && (
              <Button
                onClick={handleDelete}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Notes
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Notes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToothNotesModal;